import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import { getImageProps } from "next/image";
import { mediaRefUrl } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";

type MagnifierGlassProps = {
    image: MediaRef;
    imageSize: { width: number, height: number };
    mousePosition?: { x: number; y: number; mouseX: number; mouseY: number };
    zoomLevel: number;
    size: number;
    onError?: (error: string) => void;
    glassRef?: RefObject<HTMLDivElement | null>;
};

export default function MagnifierGlass({
    image,
    imageSize = { width: 0, height: 0 },
    mousePosition,
    zoomLevel,
    size,
    onError,
    glassRef,
}: MagnifierGlassProps) {
    const imageWidth = Math.max(1, Math.round(imageSize.width || 1));
    const imageHeight = Math.max(1, Math.round(imageSize.height || 1));
    const imageUrl = mediaRefUrl(image);

    const { props: { srcSet } } = getImageProps({
        alt: '',
        width: imageWidth,
        height: imageHeight,
        src: imageUrl,
    });

    const getBackgroundImage = useCallback((nextSrcSet = '') => {
        const imageSet = nextSrcSet
            .split(', ')
            .map((value) => {
                const [url, dpi] = value.split(' ');
                return `url("${url}") ${dpi}`;
            })
            .join(', ');
        return `image-set(${imageSet})`;
    }, []);

    const preloadUrl = useMemo(() => {
        if (!srcSet) return imageUrl;
        const parts = srcSet.split(',').map((value) => value.trim());
        const last = parts[parts.length - 1];
        const first = parts[0];
        const pick = last || first;
        const [url] = pick.split(' ');
        return url || imageUrl;
    }, [imageUrl, srcSet]);

    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        setLoaded(false);
        if (typeof window === 'undefined') return;
        if (!preloadUrl) return;
        const img = new window.Image();
        img.decoding = 'async';
        img.loading = 'eager';
        img.src = preloadUrl;
        const onLoad = () => setLoaded(true);
        const onLoadError = () => {
            onError?.("Fehler beim Laden der Lupe!");
            setLoaded(true);
        };
        img.addEventListener('load', onLoad);
        img.addEventListener('error', onLoadError);
        return () => {
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onLoadError);
        };
    }, [onError, preloadUrl]);

    const fallbackBackgroundPosition = mousePosition
        ? `${mousePosition.x}px ${mousePosition.y}px`
        : 'var(--media-view-magnifier-bg-x, 0px) var(--media-view-magnifier-bg-y, 0px)';

    const fallbackTransform = mousePosition
        ? `translate3d(${mousePosition.mouseX - (size / 2)}px, ${mousePosition.mouseY - (size / 2)}px, 0)`
        : 'translate3d(var(--media-view-magnifier-glass-left, -9999px), var(--media-view-magnifier-glass-top, -9999px), 0)';

    return (
        <Box
            ref={glassRef}
            className="magnifier-glass"
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                display: 'block',
                transform: fallbackTransform,
                backgroundImage: loaded ? getBackgroundImage(srcSet) : 'none',
                backgroundSize: `${imageWidth * zoomLevel}px ${imageHeight * zoomLevel}px`,
                backgroundPosition: fallbackBackgroundPosition,
                width: `${size}px`,
                height: `${size}px`,
                alignContent: 'center',
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: '50%',
                backgroundRepeat: 'no-repeat',
                zIndex: 300,
                pointerEvents: 'none',
                willChange: 'transform, background-position',
                border: (theme) => `4px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.9),
            }}
        >
            {!loaded && <LoadingSpinner />}
        </Box>
    );
}
